import { useState } from "react"
import { useAuth } from "../contexts/AuthContext"

interface AuthModalProps {
  onClose: () => void
}

export default function AuthModal({ onClose }: AuthModalProps) {
  const { login, register } = useAuth()
  const [mode, setMode] = useState<"login" | "register">("login")
  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shake, setShake] = useState(false)

  const triggerShake = () => {
    setShake(true)
    setTimeout(() => setShake(false), 500)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      if (mode === "login") {
        await login(username, password)
      } else {
        await register(username, email, password)
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
      triggerShake()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4">
      <form
        onSubmit={handleSubmit}
        className={`bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm space-y-4 ${shake ? "animate-shake" : ""}`}
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center">
          {mode === "login" ? "Log In" : "Sign Up"}
        </h2>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-center">
            <p className="text-red-600 dark:text-red-400 text-sm font-medium">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              error ? "border-red-300 bg-red-50 dark:bg-red-900/10" : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700"
            }`}
            required
          />
        </div>

        {mode === "register" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                error ? "border-red-300 bg-red-50 dark:bg-red-900/10" : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700"
              }`}
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
              error ? "border-red-300 bg-red-50 dark:bg-red-900/10" : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700"
            }`}
            required
            minLength={6}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-steel text-white py-3 rounded-xl font-medium hover:bg-steel-dark disabled:opacity-50 transition-colors"
        >
          {loading ? "..." : mode === "login" ? "Log In" : "Sign Up"}
        </button>

        <p className="text-sm text-center text-gray-600 dark:text-gray-400">
          {mode === "login" ? (
            <>
              No account?{" "}
              <button type="button" onClick={() => { setMode("register"); setError(null) }} className="text-blue-600 hover:underline font-medium">
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button type="button" onClick={() => { setMode("login"); setError(null) }} className="text-blue-600 hover:underline font-medium">
                Log in
              </button>
            </>
          )}
        </p>

        <button
          type="button"
          onClick={onClose}
          className="w-full text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-300 py-2"
        >
          Cancel
        </button>
      </form>
    </div>
  )
}
